import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SEO_CONFIG, PAGE_SEO, STRUCTURED_DATA } from '../config/seo';

interface SEOProps {
    page?: keyof typeof PAGE_SEO;
    title?: string;
    description?: string;
    keywords?: string[];
    canonicalUrl?: string;
    noIndex?: boolean;
    noFollow?: boolean;
    structuredData?: any;
    image?: string;
    imageAlt?: string;
    article?: {
        publishedTime?: string;
        modifiedTime?: string;
        author?: string;
        section?: string;
        tags?: string[];
    };
}

export default function SEO({
    page,
    title,
    description,
    keywords,
    canonicalUrl,
    noIndex = false,
    noFollow = false,
    structuredData,
    image,
    imageAlt,
    article
}: SEOProps) {
    // Get page-specific SEO data
    const pageData = page ? PAGE_SEO[page] : null;

    // Merge custom props with page defaults
    const seoTitle = title || pageData?.title || SEO_CONFIG.defaultTitle;
    const seoDescription = description || pageData?.description || SEO_CONFIG.description;
    const seoKeywords = keywords || pageData?.keywords || SEO_CONFIG.keywords;
    const seoCanonicalUrl = canonicalUrl || pageData?.canonicalUrl || '';
    const seoNoIndex = noIndex || (pageData && 'noIndex' in pageData ? pageData.noIndex : false) || false;

    // Build full URLs
    const fullCanonicalUrl = `${SEO_CONFIG.siteUrl}${seoCanonicalUrl}`;
    const seoImage = image || `${SEO_CONFIG.siteUrl}/og-image.png`;
    const seoImageAlt = imageAlt || `${SEO_CONFIG.siteName} - English Learning Platform`;

    // Generate structured data
    const getStructuredData = () => {
        if (structuredData) return structuredData;

        if (pageData && 'structuredData' in pageData && pageData.structuredData) {
            switch (pageData.structuredData) {
                case 'WebSite':
                    return STRUCTURED_DATA.website;
                case 'Course':
                    return STRUCTURED_DATA.course;
                case 'WebApplication':
                    return STRUCTURED_DATA.webApplication;
                case 'Blog':
                    return (STRUCTURED_DATA as any).blog;
                case 'Article':
                    return (STRUCTURED_DATA as any).article;
                default:
                    return STRUCTURED_DATA.organization;
            }
        }

        return STRUCTURED_DATA.organization;
    };

    return (
        <Helmet>
            {/* Google Search Console Verification */}
            {process.env.REACT_APP_GOOGLE_SITE_VERIFICATION && (
                <meta
                    name="google-site-verification"
                    content={process.env.REACT_APP_GOOGLE_SITE_VERIFICATION}
                />
            )}

            {/* Basic Meta Tags */}
            <title>{seoTitle}</title>
            <meta name="description" content={seoDescription} />
            <meta name="keywords" content={seoKeywords.join(', ')} />
            <meta name="author" content={SEO_CONFIG.author.name} />

            {/* Canonical URL */}
            <link rel="canonical" href={fullCanonicalUrl} />

            {/* Robots Meta */}
            <meta
                name="robots"
                content={`${seoNoIndex ? 'noindex' : 'index'},${noFollow ? 'nofollow' : 'follow'}`}
            />

            {/* Language and Region */}
            <meta name="language" content="en" />
            <meta name="geo.region" content="IN" />
            <meta name="geo.placename" content="India" />

            {/* Open Graph Tags */}
            <meta property="og:type" content={article ? 'article' : 'website'} />
            <meta property="og:title" content={seoTitle} />
            <meta property="og:description" content={seoDescription} />
            <meta property="og:image" content={seoImage} />
            <meta property="og:image:alt" content={seoImageAlt} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:url" content={fullCanonicalUrl} />
            <meta property="og:site_name" content={SEO_CONFIG.siteName} />
            <meta property="og:locale" content="en_US" />

            {/* Article-specific Open Graph */}
            {article && (
                <>
                    {article.publishedTime && (
                        <meta property="article:published_time" content={article.publishedTime} />
                    )}
                    {article.modifiedTime && (
                        <meta property="article:modified_time" content={article.modifiedTime} />
                    )}
                    {article.author && (
                        <meta property="article:author" content={article.author} />
                    )}
                    {article.section && (
                        <meta property="article:section" content={article.section} />
                    )}
                    {article.tags && article.tags.map(tag => (
                        <meta key={tag} property="article:tag" content={tag} />
                    ))}
                </>
            )}

            {/* Twitter Card Tags */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:site" content={SEO_CONFIG.social.twitter} />
            <meta name="twitter:creator" content={SEO_CONFIG.social.twitter} />
            <meta name="twitter:title" content={seoTitle} />
            <meta name="twitter:description" content={seoDescription} />
            <meta name="twitter:image" content={seoImage} />
            <meta name="twitter:image:alt" content={seoImageAlt} />

            {/* Additional Meta Tags for Educational Content */}
            <meta name="educational-level" content="Beginner, Intermediate, Advanced" />
            <meta name="content-type" content="Educational" />
            <meta name="rating" content="General" />
            <meta name="distribution" content="Global" />

            {/* Mobile App Tags */}
            <meta name="apple-mobile-web-app-capable" content="yes" />
            <meta name="apple-mobile-web-app-status-bar-style" content="default" />
            <meta name="apple-mobile-web-app-title" content={SEO_CONFIG.siteName} />
            <meta name="mobile-web-app-capable" content="yes" />

            {/* Security */}
            <meta name="referrer" content="origin-when-cross-origin" />

            {/* Preconnect to External Domains */}
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
            <link rel="preconnect" href="https://www.google-analytics.com" />
            <link rel="preconnect" href="https://www.googletagmanager.com" />

            {/* Structured Data */}
            <script type="application/ld+json">
                {JSON.stringify(getStructuredData())}
            </script>

            {/* Additional Educational Schema */}
            {(page === 'grammar' || page === 'sentences') && (
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "LearningResource",
                        "name": seoTitle,
                        "description": seoDescription,
                        "url": fullCanonicalUrl,
                        "learningResourceType": "Practice Exercise",
                        "educationalLevel": ["Beginner", "Intermediate", "Advanced"],
                        "inLanguage": "en",
                        "teaches": page === 'grammar' ? "English Grammar" : "English Sentence Formation",
                        "provider": {
                            "@type": "Organization",
                            "name": SEO_CONFIG.siteName,
                            "url": SEO_CONFIG.siteUrl
                        }
                    })}
                </script>
            )}

            {/* FAQ Schema for Knowledge-based Pages */}
            {page === 'dictionary' && (
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": [
                            {
                                "@type": "Question",
                                "name": "How can I improve my English vocabulary?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Practice daily with our interactive dictionary, learn new words in context, and use spaced repetition techniques."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "What is the best way to learn English words?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Combine reading, speaking, and writing practice. Our AI-powered platform provides personalized vocabulary building exercises."
                                }
                            }
                        ]
                    })}
                </script>
            )}
        </Helmet>
    );
}

// Specialized SEO components for different content types
export function ArticleSEO({
    title,
    description,
    publishedTime,
    modifiedTime,
    author = SEO_CONFIG.author.name,
    image,
    keywords = []
}: {
    title: string;
    description: string;
    publishedTime: string;
    modifiedTime?: string;
    author?: string;
    image?: string;
    keywords?: string[];
}) {
    return (
        <SEO
            title={title}
            description={description}
            keywords={keywords}
            image={image}
            article={{
                publishedTime,
                modifiedTime,
                author,
                section: "English Learning"
            }}
        />
    );
}

export function CourseSEO({
    title,
    description,
    courseLevel,
    duration,
    skills = []
}: {
    title: string;
    description: string;
    courseLevel: string;
    duration?: string;
    skills?: string[];
}) {
    return (
        <>
            <SEO
                title={title}
                description={description}
                keywords={skills}
            />
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Course",
                        "name": title,
                        "description": description,
                        "educationalLevel": courseLevel,
                        "timeRequired": duration,
                        "teaches": skills,
                        "provider": {
                            "@type": "Organization",
                            "name": SEO_CONFIG.siteName,
                            "url": SEO_CONFIG.siteUrl
                        },
                        "courseMode": "online",
                        "inLanguage": "en"
                    })}
                </script>
            </Helmet>
        </>
    );
} 